import React from 'react';
import PropTypes from 'prop-types';
import Dialog from './dialog';
import StepView from './step-view';
import ConfigProvider from '../config-provider';
// import Icon from '../icon';
import Button from '../button';

const noop = () => {};

class StepDialog extends React.Component {
  static propTypes = {
    prefix: PropTypes.string,
    rtl: PropTypes.bool,
    className: PropTypes.string,
    /**
     * 是否显示
     */
    visible: PropTypes.bool,
    /**
     * 内容
     */
    children: PropTypes.node,
    /**
     * 应用于取消按钮的属性对象
     */
    cancelProps: PropTypes.object,
    /**
     * 控制对话框关闭的方式，值可以为字符串或者布尔值，其中字符串是由以下值组成：
     * **close** 表示点击关闭按钮可以关闭对话框
     * **mask** 表示点击遮罩区域可以关闭对话框
     * **esc** 表示按下 esc 键可以关闭对话框
     * 如 'close' 或 'close,esc,mask'
     * 如果设置为 true，则以上关闭方式全部生效
     * 如果设置为 false，则以上关闭方式全部失效
     */
    closeable: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    /**
     * 对话框关闭时触发的回调函数
     * @param {String} trigger 关闭触发行为的描述字符串
     * @param {Object} event 关闭时事件对象
     */
    onClose: PropTypes.func,
    /**
     * 对话框关闭后触发的回调函数, 如果有动画，则在动画结束后触发
     */
    afterClose: PropTypes.func,
    /**
     * 是否显示遮罩
     */
    hasMask: PropTypes.bool,
    /**
     * 显示隐藏时动画的播放方式
     * @property {String} in 进场动画
     * @property {String} out 出场动画
     */
    animation: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    /**
     * 对话框弹出时是否自动获得焦点
     */
    autoFocus: PropTypes.bool,
    /**
     * 对话框对齐方式, 具体见Overlay文档
     */
    align: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    /**
     * 当对话框高度超过浏览器视口高度时，是否显示所有内容而不是出现滚动条以保证对话框完整显示在浏览器视口内，该属性仅在对话框垂直水平居中时生效，即 align 被设置为 'cc cc' 时
     */
    isFullScreen: PropTypes.bool,
    /**
     * 是否在对话框重新渲染时及时更新对话框位置，一般用于对话框高度变化后依然能保证原来的对齐方式
     */
    shouldUpdatePosition: PropTypes.bool,
    /**
     * 对话框距离浏览器顶部和底部的最小间距，align 被设置为 'cc cc' 并且 isFullScreen 被设置为 true 时不生效
     */
    minMargin: PropTypes.number,
    /**
     * 透传到弹层组件的属性对象
     */
    overlayProps: PropTypes.object,
    /**
     * 对话框的高度样式属性
     */
    height: PropTypes.string,
    /**
     * 没有内容区的padding
     */
    noBodyPadding: PropTypes.bool,
    /**
     * 当前步骤屏id，受控
     */
    current: PropTypes.string,
    /**
     * 默认步骤屏id，非受控
     */
    defaultCurrent: PropTypes.string,
    /**
     * 步骤屏发生变化时的回调
     */
    onStepChange: PropTypes.func,
    /**
     * 是否处于加载状态
     */
    loading: PropTypes.bool,
    /**
     * 加载内容
     */
    loadingTip: PropTypes.node,
  }

  static defaultProps = {
    prefix: 'next-',
    visible: false,
    closeable: 'esc,close',
    onClose: noop,
    afterClose: noop,
    hasMask: true,
    animation: {
      in: 'fadeInDown',
      out: 'fadeOutUp',
    },
    autoFocus: false,
    align: 'cc cc',
    isFullScreen: false,
    shouldUpdatePosition: false,
    minMargin: 40,
    overlayProps: {},
    noBodyPadding: false,
    onStepChange: noop,
    loading: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      current: props.current || props.defaultCurrent,
    };
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.current !== this.props.current) {
      this.setState({
        current: nextProps.current,
      });
    }
  }

  onStepChange = (current) => {
    if (!('current' in this.props)) {
      this.setState({
        current,
      });
    }
    this.props.onStepChange(current);
  }

  getViewMeta = () => {
    const { children } = this.props;
    const meta = [];
    React.Children.forEach(children, (c) => {
      if (c) {
        meta.push({
          id: c.props.id,
          title: c.props.title,
          footer: c.props.footer,
        });
      }
    });
    return meta;
  }

  checkView = () => {
    const { children } = this.props;
    let isView = true;
    if (children === null) {
      return false;
    }
    React.Children.forEach(children, (c) => {
      if (c && c.type !== StepView) {
        isView = false;
      }
    });
    return isView;
  }

  render() {
    if (!this.checkView()) {
      return null;
    }
    const { children, prefix } = this.props;
    const metas = this.getViewMeta();
    const { current } = this.state;
    const meta = metas.find(m => m.id === current) || {};
    const index = metas.findIndex(m => m.id === current);
    const child = React.Children.toArray(children).find(c => c.props.id === current);
    const title = index === 0 ?
      <span>{ meta.title }</span>
      :
      (
        <span>
          <a role="button" href="javascript: void(0);" className={`${prefix}dialog-prev`}>
            <Button.Icon
              name="chevron-left"
              type="primary"
              color="gray"
              onClick={this.onStepChange.bind(this, metas[index - 1].id)}
            />
          </a>
          <span>{meta.title}</span>
        </span>
      );
    return (
      <Dialog
        {...this.props}
        title={title}
        footer={meta.footer}
      >
        {child}
      </Dialog>
    );
  }
}

export default ConfigProvider.config(StepDialog);
